/**
 * One row of the combined saved-segment cut: that tag's membership as a share
 * of the study rows. The same rule as `build_figure_meta`'s `allSavedSegments`:
 * a tag everyone matches, or nobody matches, is not a row, and fewer than two
 * qualifying tags leaves the cut absent. Shares are not renormalized, so
 * overlapping tags do not sum to 100.
 */
export interface SampleProfileSavedSegmentShare {
  value: string
  count: number
  percentage: number
}

function percentage(count: number, total: number): number {
  if (total <= 0) {
    return 0
  }
  return Math.round((count / total) * 1000) / 10
}

/**
 * The combined saved-segment cut for the sample profile, or null when fewer
 * than two tags qualify. Order matches `build_figure_meta`: largest membership
 * first, then by name.
 */
export function combinedSavedSegmentShares(
  responses: Array<{ segment_labels?: unknown }>,
): Array<SampleProfileSavedSegmentShare> | null {
  const rows = responses || []
  const total = rows.length
  if (total === 0) {
    return null
  }

  const memberCounts = new Map<string, number>()
  for (const row of rows) {
    const labels = Array.isArray(row?.segment_labels) ? row.segment_labels : []
    const seen = new Set<string>()
    for (const label of labels) {
      if (typeof label !== 'string') {
        continue
      }
      const tagName = label.trim()
      if (!tagName || seen.has(tagName)) {
        continue
      }
      seen.add(tagName)
      memberCounts.set(tagName, (memberCounts.get(tagName) || 0) + 1)
    }
  }

  const qualifying = [...memberCounts.entries()]
    .filter(([, memberCount]) => memberCount > 0 && memberCount < total)
    .sort(([leftName, leftCount], [rightName, rightCount]) => {
      if (rightCount !== leftCount) {
        return rightCount - leftCount
      }
      return leftName.localeCompare(rightName, undefined, { sensitivity: 'base' })
    })

  if (qualifying.length < 2) {
    return null
  }

  return qualifying.map(([tagName, memberCount]) => ({
    value: tagName,
    count: memberCount,
    percentage: percentage(memberCount, total),
  }))
}
